import { useTheme, Typography, Paper, CircularProgress, LinearProgress, ImageList, ImageListItem, Link, useMediaQuery, IconButton, SpeedDial, SpeedDialIcon, SpeedDialAction, styled, Alert, Skeleton } from "@mui/material";
import { Box } from "@mui/system";
import { IValueAccessor } from "../utils/ValueAccessor";
import { ChangeEvent, useState } from "react";
import { BrokenImage, Delete, FileOpen, PhotoCamera } from "@mui/icons-material";
import { VisuallyHiddenInput } from "./VisuallyHiddenInput";
import Compressor from 'compressorjs';
import axios from 'axios';

export interface OutputMediasProps {
    readonly title: string;
    readonly readonly: boolean;
    readonly fileName: string;
    readonly valueAccessor: IValueAccessor<ReadonlyArray<string>> | undefined;
}

const ThumbnailImage = ({ src, alt }: { src: string; alt: string }) => {
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);
    return (
        <Box sx={{ minHeight: 80 }}>
            {!loaded && !error && <Skeleton variant="rectangular" width="100%" height={120} />}
            {error && (
                <Box sx={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
                    <BrokenImage color="disabled" />
                </Box>
            )}
            <img
                alt={alt}
                src={src}
                onLoad={() => setLoaded(true)}
                onError={() => setError(true)}
                style={{ width: '100%', height: 'auto', display: loaded ? 'block' : 'none' }}
            />
        </Box>
    );
};

const toCompressedFormData = (file: File, options: Compressor.Options): Promise<FormData> =>
    new Promise((resolve) =>
        new Compressor(file, {
            ...options,
            success: (result) => {
                const fd = new FormData();
                fd.append('file', result, (result as File).name);
                resolve(fd);
            },
        })
    );

const toFormData = (file: File): FormData => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    return fd;
};

export const OutputMedias = (props: OutputMediasProps) => {
    const { valueAccessor, title, fileName, readonly } = props;
    const baseApiUrl = "/api";
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState<string | undefined>(undefined);
    const theme = useTheme();
    const matchDownMd = useMediaQuery(theme.breakpoints.down('sm'));
    const columnCount = matchDownMd ? 1 : 2;

    const handleFileUpload = async (formDataPromise: Promise<FormData>) => {
        setProgress(0);
        setError(undefined);
        setUploading(true);
        try {
            const formData = await formDataPromise;
            const response = await axios.post<ReadonlyArray<{ id: string }>>(
                `${baseApiUrl}/Files`,
                formData,
                { onUploadProgress: (e) => { if (e.total) setProgress((e.loaded / e.total) * 100); } }
            );
            if (response.status !== 200) {
                setError("Impossible d'envoyer le fichier. Vérifiez votre connexion.");
            } else {
                const data = response.data;
                if (data.length !== 1) throw new Error("Unexpected");
                if (!valueAccessor) throw new Error("Unexpected");
                valueAccessor.set([...valueAccessor.get(), data[0].id]);
            }
        } catch (e) {
            setError("Impossible d'envoyer le fichier. Vérifiez votre connexion.");
        } finally {
            setUploading(false);
            setProgress(0);
        }
    };

    const handleBrowseChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        const formDataPromise = file.type.startsWith('image/') && file.size > 1048576 * 2 // 2 Mo
            ? toCompressedFormData(file, {
                quality: 0.8, // valeur par défaut de compressorjs
                convertSize: 1048576 * 2,
            })
            : Promise.resolve(toFormData(file));
        handleFileUpload(formDataPromise);
    };

    const handleCameraChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        handleFileUpload(toCompressedFormData(file, {
            quality: 0.8, // valeur par défaut de compressorjs
            maxWidth: 2048,
            maxHeight: 2048,
            convertSize: 1048576 * 2,
        }));
    };

    return <Box>
    <Typography variant="body1">
        {title}
    </Typography>
    {error && <Alert severity="error" onClose={() => setError(undefined)} sx={{mt: 1}}>{error}</Alert>}
    <Box sx={{textAlign: "center"}}>
        {!valueAccessor &&
        <Box sx={{ display: 'flex', p: 1 }} justifyContent="center">
            <CircularProgress />
        </Box>}
        {valueAccessor &&
        <Box sx={{position: "relative"}}>
        <ImageList cols={columnCount}>
            {valueAccessor.get().map((x, i) => {
                const alt = `Photo n°${i}`;
                const url = `${baseApiUrl}/Files/${x}?fileName=${encodeURIComponent(`${alt} ${fileName}`.replace(/^\s+|\s+$/gm, ''))}`;
                return <ImageListItem key={i}>
                    <Paper sx={{p: 1}} variant="outlined">
                        <Box sx={{position: "relative"}}>
                            <Link href={url}>
                                <ThumbnailImage alt={alt} src={`${url}&thumbnail=xs`} />
                            </Link>
                            {!readonly && <IconButton aria-label="delete"
                                onClick={() => {
                                    const newValue = [...valueAccessor.get()];
                                    newValue.splice(i, 1);
                                    valueAccessor.set(newValue);
                                }}
                                sx={{
                                position: "absolute",
                                right: 0,
                                top: 0,
                                p: 0.3,
                            }}>
                                <Delete />
                            </IconButton>}
                        </Box>
                    </Paper>
                </ImageListItem>;
            })}
            <ImageListItem key="new">
                { uploading && <LinearProgress variant={progress > 0 ? 'determinate' : 'indeterminate'} value={progress} />}
            </ImageListItem>
        </ImageList>
        { !uploading && valueAccessor.get().length === 0 && <Typography variant="body2">
                    Aucun document
                </Typography>}
        {!uploading && !readonly && <StyledSpeedDial
            ariaLabel="ajout"
            icon={<SpeedDialIcon />}
        >
            <SpeedDialAction
                icon={<IconButton component="label" aria-label="parcourir" disabled={uploading}>
                <FileOpen />
                <VisuallyHiddenInput type="file" onChange={handleBrowseChange} />
            </IconButton>}
                tooltipTitle="Parcourir"
            />
            <SpeedDialAction
                icon={<IconButton component="label" aria-label="photo" disabled={uploading}>
                <PhotoCamera />
                <VisuallyHiddenInput type="file" accept="image/*" capture onChange={handleCameraChange} />
            </IconButton>}
                tooltipTitle="Photo"
            />
        </StyledSpeedDial>}
        </Box>}
    </Box>
  </Box>;
}

export const StyledSpeedDial = styled(SpeedDial)(({ theme }) => ({
  position: 'fixed',
  zIndex: 1001,
  bottom: theme.spacing(3),
  left: 0,
  right: 0,
  marginLeft: "auto",
  marginRight: "auto",
}));
